import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Field, Form } from 'formik';
import cn from 'classnames';
import ModalPopup from 'components/organisms/modal';
import TextArea from 'components/atoms/textarea';
import Button from 'components/atoms/button';
import { showResult } from 'utils';
import useUser from 'utils/useUser';
import {
  hasAdminPanelAccess,
  hasClinicalRole,
  hasPatientAcceptanceRole,
} from 'utils/accessManager/helpers';
import { isAdmin } from 'utils/userTypeHelper';

const GeneralCommentModal = ({
  isOpen,
  toggle,
  patientGuid,
  loading,
  submit,
  comment,
  resetPcctValue,
  resetCommentValue,
  isCcmPanel,
  isRpmPanel,
  isAdminPanel,
}) => {
  const { roleNames, userType } = useUser();
  const showSwitcher = useMemo(() => {
    return (
      hasPatientAcceptanceRole(roleNames) ||
      hasAdminPanelAccess(roleNames) ||
      hasClinicalRole(roleNames) ||
      isAdmin(userType)
    );
  }, [roleNames, userType]);
  const [commentType, setCommentType] = useState('rpm');
  useMemo(() => {
    if (isRpmPanel) setCommentType('rpm');
    if (isCcmPanel) setCommentType('ccm');
  }, [isCcmPanel, isRpmPanel]);
  const handleSubmit = async (values) => {
    const postData = {
      patientGuid,
      type: 'text',
      message: values.comment,
      commentType,
    };
    const { error } = await submit(postData);
    showResult(error, null, toggle);
    resetPcctValue();
    resetCommentValue();
  };
  const handleCommentType = () => {
    if (commentType === 'rpm') setCommentType('ccm');
    else setCommentType('rpm');
  };
  return (
    <ModalPopup
      isOpen={isOpen}
      toggle={toggle}
      title="General Comment"
      className="popup general-comment-popup"
    >
      <Formik initialValues={{ comment }} onSubmit={handleSubmit}>
        {({ handleChange, values }) => (
          <Form>
            <Field
              onChange={handleChange}
              id="comment"
              name="comment"
              type="comment"
              as={TextArea}
              rows="8"
              value={values.comment}
            />
            {showSwitcher && isAdminPanel && (
              <div className="general-comments-comment-type">
                Comment Type:{' '}
                <div
                  className="general-comments-switch-button"
                  onClick={handleCommentType}
                >
                  <span
                    className={cn('general-comments-switch-item', {
                      'general-comments-switch-item-active':
                        commentType === 'rpm',
                    })}
                  >
                    RPM
                  </span>
                  <span
                    className={cn('general-comments-switch-item', {
                      'general-comments-switch-item-active':
                        commentType === 'ccm',
                    })}
                  >
                    CCM
                  </span>
                </div>
              </div>
            )}
            <div className="text-center">
              <Button type="submit" text="Send" disabled={loading} />
            </div>
          </Form>
        )}
      </Formik>
    </ModalPopup>
  );
};
GeneralCommentModal.defaultProps = {
  isOpen: false,
};

GeneralCommentModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  patientGuid: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  comment: PropTypes.string,
  resetPcctValue: PropTypes.func,
  resetCommentValue: PropTypes.func,
  isCcmPanel: PropTypes.bool,
  isRpmPanel: PropTypes.bool,
  isAdminPanel: PropTypes.bool,
};

export default GeneralCommentModal;
